import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, errorText, type EventDto, type OccurrenceDto } from "../api/client";
import { addDays, formatLongDate, formatTimeRange } from "../format";
import { calendarPersonIds } from "../calendarAudience";
import { useSession } from "../session";
import { importedSourceLabel } from "../importedSources";
import { linkParts, mapLinks } from "../maps";
import { schoolDetails, timetableChange, timetableClasses } from "../timetablePresentation";
import { PersonChips } from "./EventList";

/** Die ausgewählte Wiederholung anzeigen, zum Bearbeiten aber den versionierten Seriendatensatz verwenden. */
export function EventDetails({ occurrence, canEdit, onEdit, onClose }: {
  occurrence: OccurrenceDto; canEdit: boolean; onEdit: (event: EventDto) => void; onClose: () => void;
}) {
  const { me } = useSession();
  const dialog = useRef<HTMLDialogElement>(null);
  const event = useQuery({ queryKey: ["event", occurrence.eventId], queryFn: () => api<EventDto>(`/api/v1/events/${occurrence.eventId}`) });
  useEffect(() => { dialog.current?.showModal(); }, []);
  const school = (event.data?.source ?? occurrence.source) === "web_untis";
  const change = timetableChange(occurrence.source, occurrence.title);
  const origin = importedSourceLabel(event.data?.source ?? occurrence.source);
  // Stundenplan-Orte sind Räume („Raum 2“) – dafür keine Karte anbieten.
  const maps = school ? null : mapLinks(occurrence.location);
  return (
    <dialog ref={dialog} role="dialog" className="dialog card event-details" aria-label="Termindetails" onCancel={onClose} onClose={onClose}>
      <div className="row between"><span className="muted small">{school ? "Stundenplan · WebUntis" : origin ? `Termin · automatisch von ${origin}` : "Termin"}</span><button className="btn ghost" onClick={onClose}>Schließen</button></div>
      <h2>{occurrence.title}</h2>
      {change && <p className={`change-explanation${timetableClasses(occurrence.source, occurrence.title)}`}>
        {change === "cancelled" ? "Diese Unterrichtsstunde entfällt." : "Die Schule hat diesen Unterricht geändert. Details und frühere Angaben stehen unten."}
      </p>}
      <p>{formatLongDate(occurrence.start)}{occurrence.allDay && occurrence.end && addDays(occurrence.end, -1) > occurrence.start.slice(0, 10) ? ` – ${formatLongDate(addDays(occurrence.end, -1))}` : ""}<br />{formatTimeRange(occurrence.start, occurrence.end, occurrence.allDay)}</p>
      <PersonChips ids={calendarPersonIds(occurrence.participants.map(p => p.personId), me.persons)} persons={me.persons} />
      {occurrence.location && (maps ? (
        <div className="event-place">
          <a className="place-link" href={maps.primary.href} target="_blank" rel="noopener noreferrer" aria-label={`${occurrence.location} in ${maps.primary.label} öffnen`}>
            <span aria-hidden="true">📍</span>
            <span className="grow">{occurrence.location}</span>
            <span className="place-open">Karte öffnen ›</span>
          </a>
          <a className="small muted" href={maps.alternative.href} target="_blank" rel="noopener noreferrer">oder in {maps.alternative.label}</a>
        </div>
      ) : <p>Ort: {occurrence.location}</p>)}
      {event.isPending && <p role="status">Details werden geladen …</p>}
      {event.isError && <p className="error">{errorText(event.error)}</p>}
      {school ? <>
        <dl className="school-details">{schoolDetails(event.data?.description ?? null).map((item, i) => <div key={i}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}</dl>
        <p className="muted small">Automatisch aus WebUntis übernommen. Änderungen erfolgen über die Schule.</p>
        {event.data?.description && <details><summary>Quelldaten anzeigen</summary><pre className="source-data">{event.data.description}</pre></details>}
      </> : event.data?.description && <p className="event-note">{linkParts(event.data.description).map((part, i) => part.href
        ? <a key={i} href={part.href} target="_blank" rel="noopener noreferrer">{part.text}</a>
        : <span key={i}>{part.text}</span>)}</p>}
      {event.data?.recurrence && <p className="muted small">Serientermin · Bearbeitung gilt für die Serie.</p>}
      {!school && canEdit && event.data && <button className="btn primary" onClick={() => onEdit(event.data!)}>Ändern</button>}
    </dialog>
  );
}
